import { paidOf } from '../../api/plannerApi';
import { money } from '../../api/plannerApi';
import { isPayable, TAG_PALETTE } from '../../types/board';
import type { Board, Item } from '../../types/board';

export default function ItemCard({
  item,
  board,
  onOpen,
  onDragStart,
}: {
  item: Item;
  board: Board;
  onOpen: () => void;
  onDragStart: (e: React.DragEvent) => void;
}) {
  const paid = paidOf(item);
  const exp = item.expectedPrice || 0;
  const pct = exp > 0 ? Math.min(100, Math.round((paid / exp) * 100)) : 0;
  const metaParts = [item.vendor, item.dueDate ? `Due ${item.dueDate}` : ''].filter(Boolean);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onOpen}
      className="bg-card border border-line px-3.5 pt-3.5 pb-3 cursor-pointer transition-shadow hover:shadow-[0_2px_10px_oklch(24%_0.02_50_/_0.08)]"
      style={{ borderLeft: `3px solid ${item.priority ? 'oklch(50% 0.1 20)' : 'transparent'}` }}
    >
      <div className="font-serif italic text-base mb-1.5">{item.title}</div>

      {item.categoryIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {item.categoryIds.map((cid) => {
            const idx = board.categories.findIndex((c) => c.id === cid);
            const cat = board.categories[idx];
            if (!cat) return null;
            const colors = TAG_PALETTE[idx % TAG_PALETTE.length];
            return (
              <span
                key={cid}
                className="text-[10px] font-semibold uppercase tracking-wide rounded px-2 py-0.5"
                style={{ background: colors.bg, color: colors.text }}
              >
                {cat.name}
              </span>
            );
          })}
        </div>
      )}

      {metaParts.length > 0 && <div className="text-xs text-muted mb-2">{metaParts.join(' · ')}</div>}

      {isPayable(item.status) ? (
        <>
          <div className="h-[5px] rounded-full bg-[oklch(92%_0.012_60)] overflow-hidden mb-1">
            <div className="h-full rounded-full bg-sage" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-[11px] tabular-nums text-muted">
            <span>{money(paid)} paid</span>
            <span>{money(exp)}</span>
          </div>
        </>
      ) : (
        <div className="text-[11px] tabular-nums text-muted">{money(exp)}</div>
      )}

      {(item.notes || item.attachments.length > 0) && (
        <div className="flex gap-1.5 mt-2">
          {item.notes && (
            <span className="text-[10px] rounded px-1.5 py-0.5 text-muted bg-[oklch(92%_0.012_60)]">Notes</span>
          )}
          {item.attachments.length > 0 && (
            <span className="text-[10px] rounded px-1.5 py-0.5 text-terra bg-[oklch(94%_0.03_20)]">
              {item.attachments.length} attachment(s)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
